import React, { useRef } from "react";
import { useHighlightCode } from "../utils/utility";
import {
  isMCQType, CODE_QUESTION_TYPE, typeLabel, DIFFICULTY_TONE,
  renderContent, hasContent, formatDateTime, readable, sectionTypeLabel,
} from "./questionUtils";
import "../../styles/QuestionView.css";

const API_URL = process.env.REACT_APP_API_URL;
const OPTION_LETTERS = "ABCDEFGHIJ";

// Legacy attachments are stored as /uploads/… on the API host.
const imgSrc = (path) => (path && path.startsWith("/uploads/") ? `${API_URL}${path}` : path);

const Rich = ({ html, className = "" }) => (
  <div className={`qv-rich ${className}`} dangerouslySetInnerHTML={{ __html: renderContent(html) }} />
);

const Attachment = ({ src, alt }) => (src ? <img className="qv-attachment" src={imgSrc(src)} alt={alt} /> : null);

const Card = ({ title, aside, children, className = "" }) => (
  <section className={`qv-card ${className}`}>
    {(title || aside) && (
      <header className="qv-card-head">
        {title && <h3>{title}</h3>}
        {aside}
      </header>
    )}
    {children}
  </section>
);

const MetaRow = ({ label, children }) => (
  <div className="qv-meta-row">
    <dt>{label}</dt>
    <dd>{children || <span className="qv-muted">—</span>}</dd>
  </div>
);

// Everything about one question, read-only. Used by the details page and the
// preview modal on the question list, so the two can never show it differently.
const QuestionView = ({ question: q, compact = false }) => {
  const ref = useRef(null);
  useHighlightCode(ref, [q]);
  if (!q) return null;

  const isMCQ = isMCQType(q.questionType);
  const options = Array.isArray(q.options) ? q.options : [];
  const correctIds = isMCQ ? String(q.answer || "").split(",").map((s) => s.trim()).filter(Boolean) : [];
  const testCases = q.questionType === CODE_QUESTION_TYPE && Array.isArray(q.testCases) ? q.testCases : [];
  const starterCode = q.questionType === CODE_QUESTION_TYPE ? String(q.starterCode || "") : "";
  const alternates = !isMCQ && Array.isArray(q.alternateAnswers) ? q.alternateAnswers.filter(Boolean) : [];
  const rejected = !isMCQ && Array.isArray(q.rejectedAnswers) ? q.rejectedAnswers.filter(Boolean) : [];
  const connections = Array.isArray(q.connectedWith) ? q.connectedWith : [];
  const subject = readable(q.subjectTitle || q.subject);
  const mod = readable(q.moduleTitle || q.module);
  const topic = readable(q.topicTitle || q.topic);

  return (
    <div className={`qv${compact ? " qv-compact" : ""}`} ref={ref}>
      <div className="qv-chips">
        <span className="qv-chip qv-chip-type">{typeLabel(q.questionType)}</span>
        {q.difficultyLevel && (
          <span className={`qv-chip qv-diff qv-diff-${DIFFICULTY_TONE[q.difficultyLevel] || "none"}`}>{q.difficultyLevel}</span>
        )}
        {(q.labels || []).map((l) => <span key={l.value} className="qv-chip qv-chip-label">{l.label}</span>)}
      </div>

      <div className="qv-grid">
        <div className="qv-main">
          <Card title="Question">
            {hasContent(q.question) ? <Rich html={q.question} /> : <p className="qv-muted">No question text.</p>}
            <Attachment src={q.questionAttachment} alt="Question attachment" />
          </Card>

          {isMCQ ? (
            <Card title="Options" aside={<span className="qv-card-note">{correctIds.length > 1 ? `${correctIds.length} correct` : "Correct option highlighted"}</span>}>
              {options.length ? (
                <ol className="qv-options">
                  {options.map((o, i) => {
                    const correct = correctIds.includes(String(o._id));
                    return (
                      <li key={o._id} className={`qv-option${correct ? " is-correct" : ""}`}>
                        <span className="qv-option-letter">{OPTION_LETTERS[i] || i + 1}</span>
                        <div className="qv-option-body">
                          {o.option && <Rich html={o.option} />}
                          <Attachment src={o.optionAttachment} alt={`Option ${OPTION_LETTERS[i]}`} />
                        </div>
                        {correct && <span className="qv-option-tick" aria-label="Correct">✓ Correct</span>}
                      </li>
                    );
                  })}
                </ol>
              ) : <p className="qv-muted">No options added.</p>}
            </Card>
          ) : (
            <Card title={q.questionType === CODE_QUESTION_TYPE ? "Expected output" : "Answer"}>
              {hasContent(q.answer) ? <Rich html={q.answer} /> : <p className="qv-muted">No answer added.</p>}
              <Attachment src={q.answerAttachment} alt="Answer attachment" />
              {alternates.length > 0 && (
                <div className="qv-alternates">
                  <div className="qv-sublabel">Also accepted</div>
                  <div className="qv-alternate-list">
                    {alternates.map((a, i) => <span key={i} className="qv-alternate">{a}</span>)}
                  </div>
                </div>
              )}
              {rejected.length > 0 && (
                <div className="qv-alternates">
                  <div className="qv-sublabel">Marked incorrect</div>
                  <div className="qv-alternate-list">
                    {rejected.map((a, i) => <span key={i} className="qv-alternate qv-rejected">{a}</span>)}
                  </div>
                </div>
              )}
            </Card>
          )}

          {starterCode.trim() && (
            <Card title="Starter code" aside={<span className="qv-card-note">What the student's answer box starts with</span>}>
              <pre className="qv-pre">{starterCode}</pre>
            </Card>
          )}

          {testCases.length > 0 && (
            <Card title="Test cases" aside={<span className="qv-card-note">{testCases.length} of 3</span>}>
              <div className="qv-tests">
                {testCases.map((c, i) => (
                  <div className="qv-test" key={i}>
                    <div className="qv-test-title">Case {i + 1}</div>
                    <div className="qv-test-io">
                      <div>
                        <div className="qv-sublabel">Input</div>
                        <pre className="qv-pre">{c.input?.trim() ? c.input : <span className="qv-muted">(no input)</span>}</pre>
                      </div>
                      <div>
                        <div className="qv-sublabel">Expected output</div>
                        <pre className="qv-pre">{c.expectedOutput?.trim() ? c.expectedOutput : <span className="qv-muted">—</span>}</pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="Solution">
            {hasContent(q.answerExplanation) ? <Rich html={q.answerExplanation} /> : <p className="qv-muted">No solution added.</p>}
          </Card>
        </div>

        <aside className="qv-side">
          <Card title="Classification">
            <dl className="qv-meta">
              <MetaRow label="Subject">{subject}</MetaRow>
              <MetaRow label="Module">{mod}</MetaRow>
              <MetaRow label="Topic">{topic}</MetaRow>
            </dl>
          </Card>

          <Card title="Connected with" aside={connections.length > 0 && <span className="qv-card-note">{connections.length}</span>}>
            {connections.length ? (
              <ul className="qv-links">
                {connections.map((c) => (
                  <li key={c.sectionId}>
                    <div className="qv-link-path">
                      {c.moduleTitle && <span>{c.moduleTitle}</span>}
                      <span>{c.topicTitle}</span>
                    </div>
                    <div className="qv-link-section">
                      {c.sectionTitle}
                      {c.sectionType && <span className="qv-chip qv-chip-mini">{sectionTypeLabel(c.sectionType)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p className="qv-muted">Not used in any notes section yet.</p>}
          </Card>

          <Card title="Details">
            <dl className="qv-meta">
              <MetaRow label="Marks">
                {(Number(q.markPositive) || Number(q.markNegative))
                  ? <span><b className="qv-pos">+{Number(q.markPositive) || 0}</b> / <b className="qv-neg">−{Number(q.markNegative) || 0}</b></span>
                  : ""}
              </MetaRow>
              <MetaRow label="Notes">{readable(q.notes)}</MetaRow>
              <MetaRow label="Reference">
                {q.questionLink ? <a href={q.questionLink} target="_blank" rel="noopener noreferrer" className="qv-link">Open link ↗</a> : ""}
              </MetaRow>
              <MetaRow label="Created">{formatDateTime(q.createdDateTime)}</MetaRow>
              <MetaRow label="Updated">{formatDateTime(q.updatedDateTime)}</MetaRow>
            </dl>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default QuestionView;
